// Icons home / league-creation screen (Hardwood GM design). Replaces ZenGM's Dashboard: brand
// block, "Continue a league" saved-game rows, and a 30-team grid to start a new league. Our
// product hides season/phase selection — every new league is seeded at the latest real state
// (2026, after the draft) via the worker's createRealLeague, so the user only picks a franchise.

import type { CSSProperties } from "react";
import { useEffect, useId, useMemo, useRef, useState } from "react";
import useTitleBar from "../hooks/useTitleBar.tsx";
import { toWorker } from "../util/toWorker.ts";
import { realtimeUpdate } from "../util/realtimeUpdate.ts";
import { logEvent } from "../util/logEvent.ts";
import type { View } from "../../common/types.ts";
import type { NewLeagueTeam } from "./NewLeague/types.ts";
import SummerLeagueMark from "../components/SummerLeagueMark.tsx";
import {
	ChallengeCreatedSheet,
	ChallengeJoinSheet,
	ChallengeVerify,
} from "../components/ChallengeSheets.tsx";
import {
	challengeEntryEnabled,
	challengeInviteText,
} from "../util/challengeUi.ts";

// Two-stop diagonal gradient from a team's primary color (design's shade(c, ±amt) recipe: clamp
// each RGB channel by amt).
const shade = (hex: string, amt: number) => {
	const h = hex.replace("#", "");
	const full =
		h.length === 3
			? h
					.split("")
					.map((c) => c + c)
					.join("")
			: h;
	const n = Number.parseInt(full, 16);
	if (Number.isNaN(n)) {
		return hex;
	}
	const clamp = (v: number) => Math.max(0, Math.min(255, v));
	const r = clamp((n >> 16) + amt);
	const g = clamp(((n >> 8) & 255) + amt);
	const b = clamp((n & 255) + amt);
	return `rgb(${r},${g},${b})`;
};
const crest = (color: string) =>
	`linear-gradient(150deg, ${shade(color, 26)}, ${shade(color, -34)})`;

// White abbr text is unreadable on a light-primary crest (the fork has a couple, e.g. Boston).
// Fall back to near-black text when the color is bright.
const crestText = (hex: string) => {
	const h = hex.replace("#", "");
	const full =
		h.length === 3
			? h
					.split("")
					.map((c) => c + c)
					.join("")
			: h;
	const n = Number.parseInt(full, 16);
	if (Number.isNaN(n)) {
		return "#fff";
	}
	const r = n >> 16;
	const g = (n >> 8) & 255;
	const b = n & 255;
	const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
	return luminance > 0.62 ? "#0a0a0a" : "#fff";
};

// Save-slot cap: how many leagues can exist at once.
const MAX_LEAGUES = 3;

type DashboardView = View<"dashboard">;

const games = [
	{
		accent: "#C084FC",
		description:
			"Reveal random teams one at a time, pick one player from each roster, then simulate the season.",
		href: "/team-reveal",
		kicker: "Team Reveal",
		meta: ["Multiplayer", "Random teams", "Season sim"],
		name: "Team Reveal",
		time: "2 min",
	},
	{
		accent: "#18E0A8",
		description:
			"Draft five current players under a cap, submit your build, and compare results with friends.",
		href: "/fantasy",
		kicker: "Cap Lineup",
		meta: ["Multiplayer", "5 picks", "Shareable board"],
		more: true,
		name: "5 Player Draw",
		time: "1 min",
	},
	{
		accent: "#FF6B7D",
		description:
			"Reveal one tier at a time, pick your five, then simulate the season.",
		href: "/fantasy-reveal",
		kicker: "Reveal Draft",
		meta: ["Multiplayer", "Tier reveal", "Season sim"],
		more: true,
		name: "5 Player - Reveal",
		time: "2 min",
	},
	{
		accent: "#33B6FF",
		description:
			"Draft five current players, simulate a full season and playoff games.",
		href: "/fantasy-sim",
		kicker: "Expanded Sim",
		meta: ["Multiplayer", "Monthly scores", "Playoff path"],
		name: "5 Player Draw - Sim",
		time: "2 min",
	},
	{
		accent: "#E8B923",
		description:
			"Pick one franchise, invite friends, and see who manages the same frozen season best.",
		href: "/challenge",
		kicker: "Executive Challenge",
		meta: ["Multiplayer", "Same team", "Best GM wins"],
		more: true,
		name: "Single Season Challenge",
		time: "30 min",
	},
	{
		accent: "#7C9FF0",
		description:
			"Take over a team, make the draft and free-agency calls, and chase a long-term run.",
		href: "/dynasty",
		kicker: "Full GM Mode",
		meta: ["30 teams", "Draft", "Free agency"],
		name: "Dynasty",
		time: "1h",
	},
];

const SiteHub = () => {
	useTitleBar({ title: "Summer League", hideNewWindow: true });
	const [showMore, setShowMore] = useState(false);
	const primaryGames = games.filter((game) => !game.more);
	const moreGames = games.filter((game) => game.more);

	return (
		<div className="sl-hub">
			<style>{hubStyles}</style>
			<div className="sl-hub-shell">
				<header className="sl-hub-head">
					<a className="sl-hub-brand" href="/">
						<SummerLeagueMark size={44} />
						<span>
							<b>SUMMER</b>
							<i>LEAGUE</i>
						</span>
					</a>
				</header>

				<section className="sl-hub-intro">
					<div className="sl-hub-kicker">Pick a game</div>
					<h1>Three ways to prove you know ball.</h1>
				</section>

				<div className="sl-game-grid">
					{primaryGames.map((game) => (
						<a
							className="sl-game-card"
							href={game.href}
							key={game.name}
							style={{ "--accent": game.accent } as CSSProperties}
						>
							<span className="sl-game-time">
								<small>Est.</small>
								<strong>{game.time}</strong>
							</span>
							<span className="sl-game-copy">
								<span className="sl-game-kicker">{game.kicker}</span>
								<strong>{game.name}</strong>
								<span>{game.description}</span>
							</span>
							<span className="sl-game-meta">
								{game.meta.map((item) => (
									<small
										className={
											item === "Multiplayer"
												? "sl-game-meta-multiplayer"
												: undefined
										}
										key={item}
									>
										{item}
									</small>
								))}
							</span>
							<span className="sl-game-open">Open</span>
						</a>
					))}
				</div>
				<button
					className="sl-see-more"
					onClick={() => setShowMore((value) => !value)}
					type="button"
				>
					{showMore ? "See Less" : "See Community Games"}
				</button>
				{showMore ? (
					<div className="sl-game-grid sl-game-grid-more">
						{moreGames.map((game) => (
							<a
								className="sl-game-card"
								href={game.href}
								key={game.name}
								style={{ "--accent": game.accent } as CSSProperties}
							>
								<span className="sl-game-time">
									<small>Est.</small>
									<strong>{game.time}</strong>
								</span>
								<span className="sl-game-copy">
									<span className="sl-game-kicker">{game.kicker}</span>
									<strong>{game.name}</strong>
									<span>{game.description}</span>
								</span>
								<span className="sl-game-meta">
									{game.meta.map((item) => (
										<small
											className={
												item === "Multiplayer"
													? "sl-game-meta-multiplayer"
													: undefined
											}
											key={item}
										>
											{item}
										</small>
									))}
								</span>
								<span className="sl-game-open">Open</span>
							</a>
						))}
					</div>
				) : null}
			</div>
		</div>
	);
};

// joinCode arrives from the /c/{code} invite deep link (see routeInfos +
// processInputs.dashboard); renamed locally to avoid the joinCode ref below.
const DynastyStart = ({
	challengeRoute = false,
	leagues,
	joinCode: deepLinkCode,
}: DashboardView & { challengeRoute?: boolean }) => {
	useTitleBar({
		title: challengeRoute ? "Single Season Challenge" : "Dynasty",
	});
	const leagueCreationID = useId();

	const [teams, setTeams] = useState<NewLeagueTeam[]>([]);
	const [query, setQuery] = useState("");
	const [selected, setSelected] = useState<number | undefined>(undefined);
	const [confirmDelete, setConfirmDelete] = useState<
		View<"dashboard">["leagues"][number] | undefined
	>(undefined);
	const [creating, setCreating] = useState(false);
	const [showLimit, setShowLimit] = useState(false);

	// Executive Challenge flow. mode "challenge" turns the 30-team grid into the
	// host's "Pick the team to host" selector; the sheets carry join + verify +
	// created. verifyPath tracks whether Lock-it-in fires create or join.
	const [mode, setMode] = useState<"league" | "challenge">(
		challengeRoute ? "challenge" : "league",
	);
	const [sheet, setSheet] = useState<null | "join" | "verify" | "created">(
		null,
	);
	const [verifyPath, setVerifyPath] = useState<"create" | "join">("create");
	const [joinTid, setJoinTid] = useState<number | undefined>(undefined);
	const joinCode = useRef("");
	// The minted (but not yet built) challenge: code + what "Start managing"
	// needs to build the league. Building is deferred to that tap because the
	// phase advance redirects the tab into the league, past the created sheet.
	const [created, setCreated] = useState<
		{ code: string; playerId: string; name: string } | undefined
	>(undefined);
	// A create/join failure the user needs to see (network blip, backend
	// hiccup, half-updated cache). Without this the error was swallowed and the
	// user was silently dropped back on the home grid — indistinguishable from
	// "nothing happened". `retry` re-runs the exact operation that failed.
	const [challengeError, setChallengeError] = useState<
		{ verb: "create" | "join"; detail: string; retry: () => void } | undefined
	>(undefined);
	const [toast, setToast] = useState("");
	const toastTimeout = useRef<ReturnType<typeof setTimeout> | undefined>(
		undefined,
	);

	const flashToast = (text: string) => {
		clearTimeout(toastTimeout.current);
		setToast(text);
		toastTimeout.current = setTimeout(() => setToast(""), 2200);
	};
	useEffect(() => () => clearTimeout(toastTimeout.current), []);

	// Soft launch: the whole challenge lobby (entry card + deep links) only
	// shows on non-production origins. Evaluated once per mount — the hostname
	// can't change under a SPA.
	const challengeLobby = challengeRoute || challengeEntryEnabled();

	// Deep-link into the join flow: invite links land on /#join (sheet open,
	// empty) or /c/{code} (sheet open, code prefilled — joinCode via the
	// router). One-shot: it only applies to the landing render, and it respects
	// the save-slot cap the same way the Join button does.
	const deepLinkHandled = useRef(false);
	useEffect(() => {
		if (deepLinkHandled.current || !challengeLobby) {
			return;
		}
		deepLinkHandled.current = true;
		if (deepLinkCode || window.location.hash.toLowerCase() === "#join") {
			if (leagues.length >= MAX_LEAGUES) {
				setShowLimit(true);
			} else {
				setSheet("join");
			}
		}
	}, [challengeLobby, deepLinkCode, leagues.length]);

	// Load the 2026 franchises for the grid (same real scaffolding the worker uses to create).
	useEffect(() => {
		let cancelled = false;
		(async () => {
			// realStats MUST match createRealLeague's ("all"): it changes leagueInfo.teams (full
			// history keeps defunct franchises), and the grid's tids must live in the same team
			// space as the one the worker creates from — otherwise the user gets the wrong team.
			const info = await toWorker("main", "getLeagueInfo", {
				type: "real",
				season: 2026,
				phase: 6, // PHASE.AFTER_DRAFT
				randomDebuts: false,
				randomDebutsKeepCurrent: false,
				realDraftRatings: "rookie",
				realStats: "all",
				includePlayers: false,
			});
			if (!cancelled) {
				setTeams(
					(info.teams as NewLeagueTeam[])
						.filter((t) => !t.disabled)
						.sort((a, b) =>
							`${a.region} ${a.name}`.localeCompare(`${b.region} ${b.name}`),
						),
				);
			}
		})();
		return () => {
			cancelled = true;
		};
	}, []);

	const filtered = useMemo(() => {
		const q = query.trim().toLowerCase();
		if (!q) {
			return teams;
		}
		return teams.filter((t) =>
			`${t.abbrev} ${t.region} ${t.name}`.toLowerCase().includes(q),
		);
	}, [teams, query]);

	const teamByTid = useMemo(() => {
		const m = new Map<number, NewLeagueTeam>();
		for (const t of teams) {
			m.set(t.tid, t);
		}
		return m;
	}, [teams]);

	const selectedTeam =
		selected !== undefined ? teamByTid.get(selected) : undefined;

	const start = async () => {
		if (selectedTeam === undefined || creating) {
			return;
		}
		// Cap the save slots: only MAX_LEAGUES leagues at a time.
		if (leagues.length >= MAX_LEAGUES) {
			setShowLimit(true);
			return;
		}
		setCreating(true);
		try {
			const lid = await toWorker("main", "createRealLeague", {
				tid: selectedTeam.tid,
				name: `${selectedTeam.region} ${selectedTeam.name}`,
				leagueCreationID,
			});
			realtimeUpdate([], `/l/${lid}`);
		} catch (error) {
			// Toasts are unreliable in this shell — make sure the failure is at least inspectable.
			console.error("createRealLeague failed:", error);
			setCreating(false);
			logEvent({
				type: "error",
				text: error.message,
				persistent: true,
				saveToDb: false,
			});
		}
	};

	// Entering either challenge flow needs a free save slot (the challenge is a
	// real local league; only the paid credit is waived, not storage).
	const requireSlot = () => {
		if (leagues.length >= MAX_LEAGUES) {
			setShowLimit(true);
			return false;
		}
		return true;
	};

	const startChallengeHost = () => {
		if (!requireSlot()) {
			return;
		}
		setMode("challenge");
		setSelected(undefined);
		window.scrollTo({ top: 0 });
	};

	const openJoin = () => {
		if (!requireSlot()) {
			return;
		}
		setSheet("join");
	};

	// Host: mint the code (fast, no build) → show the created sheet.
	const doCreate = async (playerId: string) => {
		if (selectedTeam === undefined || creating) {
			return;
		}
		setChallengeError(undefined);
		try {
			const res = await toWorker("main", "mintChallengeCode", {
				tid: selectedTeam.tid,
				playerId,
			});
			setCreated({
				code: res.code,
				playerId,
				name: `${selectedTeam.region} ${selectedTeam.name}`,
			});
			setSheet("created");
		} catch (error) {
			setChallengeError({
				verb: "create",
				detail: (error as Error).message,
				retry: () => doCreate(playerId),
			});
		}
	};

	// Guest: build the frozen league and drop into it (redirect fires on
	// success). This is the slow path — a real league build behind a live
	// backend — so it's the one most likely to hit a transient failure.
	const doJoin = async (playerId: string) => {
		const code = joinCode.current;
		const t = joinTid !== undefined ? teamByTid.get(joinTid) : undefined;
		if (!code || creating) {
			return;
		}
		setChallengeError(undefined);
		setCreating(true);
		try {
			const res = await toWorker("main", "joinChallenge", {
				code,
				playerId,
				name: t ? `${t.region} ${t.name}` : undefined,
				leagueCreationID,
			});
			realtimeUpdate([], `/l/${res.lid}`);
		} catch (error) {
			setCreating(false);
			setChallengeError({
				verb: "join",
				detail: (error as Error).message,
				retry: () => doJoin(playerId),
			});
		}
	};

	// Verify sheet finished (player ID locked in) — fire the path we came from.
	const onVerified = async (playerId: string) => {
		setSheet(null);
		if (verifyPath === "create") {
			await doCreate(playerId);
		} else {
			await doJoin(playerId);
		}
	};

	// "Start managing" — actually build the challenge league. The worker's
	// phase advance redirects this tab into the league when it finishes.
	const startManaging = async () => {
		if (!created || creating) {
			return;
		}
		setSheet(null);
		setChallengeError(undefined);
		setCreating(true);
		try {
			const res = await toWorker("main", "createChallenge", {
				code: created.code,
				playerId: created.playerId,
				name: created.name,
				leagueCreationID,
			});
			realtimeUpdate([], `/l/${res.lid}`);
		} catch (error) {
			setCreating(false);
			setChallengeError({
				verb: "create",
				detail: (error as Error).message,
				// The code is already minted — retry just rebuilds the league.
				retry: startManaging,
			});
		}
	};

	const copyInvite = async (code: string) => {
		try {
			await navigator.clipboard.writeText(challengeInviteText(code));
			flashToast(`Invite link copied · ${code}`);
		} catch {
			flashToast(`Your code: ${code}`);
		}
	};

	const doDelete = async () => {
		if (!confirmDelete) {
			return;
		}
		const lid = confirmDelete.lid;
		setConfirmDelete(undefined);
		await toWorker("main", "removeLeague", lid);
		realtimeUpdate(["leagues"]);
	};

	const hasLeagues = leagues.length > 0;

	return (
		<div className="gm-start">
			<style>{styles}</style>

			{creating ? (
				<div className="gm-splash">
					<div className="gm-splash-pop">
						<div className="gm-splash-bob">
							<SummerLeagueMark size={128} />
						</div>
					</div>
					<div className="gm-splash-word">
						<b>SUMMER</b>
						<i>LEAGUE</i>
					</div>
					<div className="gm-splash-bar">
						<div className="gm-splash-fill" />
					</div>
					<div className="gm-splash-cap">TIPPING OFF…</div>
				</div>
			) : null}

			<div className="gm-start-header" />

			<div className="gm-start-body">
				<div className="gm-brand">
					<span className="gm-brand-logo">
						<SummerLeagueMark size={46} />
					</span>
					<span className="gm-brand-word">
						<b>SUMMER</b>
						<i>LEAGUE</i>
					</span>
					{!challengeRoute ? (
						<a
							className="gm-main-return"
							href="/"
							onClick={(e) => {
								e.preventDefault();
								realtimeUpdate([], "/");
							}}
						>
							All games
						</a>
					) : null}
				</div>
				{mode === "challenge" ? (
					<>
						<div className="gm-ch-top-actions">
							{challengeRoute ? (
								<a className="gm-ch-back" href="/">
									All games
								</a>
							) : (
								<button
									type="button"
									className="gm-ch-back"
									onClick={() => {
										setMode("league");
										setSelected(undefined);
									}}
								>
									‹ Back
								</button>
							)}
							<button
								type="button"
								className="gm-ch-route-join"
								onClick={openJoin}
							>
								Have a code? <span>Join</span>
							</button>
						</div>
						<div className="gm-ch-eyebrow">
							<SummerLeagueMark size={24} idSuffix="ch" />
							<span>SUMMER 2026 EXECUTIVE CHALLENGE</span>
						</div>
						<h1 className="gm-headline gm-headline-ch">
							Pick the team to host
						</h1>
						<p className="gm-subhead">
							Everyone in your challenge manages this club through the same
							frozen-luck season. Best GM wins.
						</p>
						<div className="gm-ch-free">
							Same season, same team, best GM wins
						</div>
					</>
				) : (
					<>
						<h1 className="gm-headline">Build a Dynasty</h1>
						<p className="gm-subhead">
							Pick your team, sign free agents, run the draft, and chase the
							trophy — one summer at a time.
						</p>

						{challengeLobby ? (
							<div className="gm-ch-card">
								<div className="gm-ch-spectrum" />
								<div className="gm-ch-glow" />
								<div className="gm-ch-card-body">
									<div className="gm-ch-eyebrow">
										<SummerLeagueMark size={24} idSuffix="entry" />
										<span>EXECUTIVE CHALLENGE · 2026</span>
										<span className="gm-ch-new">NEW</span>
									</div>
									<div className="gm-ch-card-title">Challenge your friends</div>
									<div className="gm-ch-card-sub">
										One season, one team, frozen luck — everyone runs the same
										club and the best GM wins.
									</div>
									<div className="gm-ch-actions">
										<button
											type="button"
											className="gm-ch-start"
											onClick={startChallengeHost}
										>
											🏆 Start Challenge
										</button>
										<button
											type="button"
											className="gm-ch-join"
											onClick={openJoin}
										>
											Have a code? <span>Join ›</span>
										</button>
									</div>
								</div>
							</div>
						) : null}
					</>
				)}

				{hasLeagues && mode === "league" ? (
					<section className="gm-section">
						<div className="gm-section-head">
							<h2>Continue a league</h2>
							<span className="gm-section-meta">{leagues.length} saved</span>
						</div>
						{leagues.map((lg, i) => {
							const t = teamByTid.get(lg.tid);
							const color = t?.colors?.[0] ?? "#1d3a52";
							const abbr = t?.abbrev ?? lg.teamName.slice(0, 3).toUpperCase();
							// firstUserSeason, not startingSeason — the latter is 1947 in leagues
							// carrying full real historical stats.
							const firstSeason = lg.firstUserSeason ?? lg.startingSeason;
							const seasons =
								firstSeason !== undefined && lg.season !== undefined
									? 1 + lg.season - firstSeason
									: undefined;
							return (
								<a
									key={lg.lid}
									href={`/l/${lg.lid}`}
									className="gm-league-row"
									style={{ animationDelay: `${i * 0.04}s` }}
								>
									<span
										className="gm-crest gm-crest-row"
										style={{
											background: crest(color),
											color: crestText(color),
										}}
									>
										{abbr}
									</span>
									<span className="gm-league-info">
										<span className="gm-league-name">
											{lg.teamRegion} {lg.teamName}
										</span>
										<span className="gm-league-metaline">
											{lg.season ?? lg.startingSeason ?? ""}
											{seasons !== undefined
												? ` · ${seasons} season${seasons === 1 ? "" : "s"} played`
												: ""}
										</span>
									</span>
									<span className="gm-play gm-play-sm" aria-label="Resume">
										<span className="gm-tri" />
									</span>
									<button
										type="button"
										className="gm-trash"
										aria-label="Delete league"
										onClick={(e) => {
											e.preventDefault();
											e.stopPropagation();
											setConfirmDelete(lg);
										}}
									>
										🗑
									</button>
								</a>
							);
						})}
					</section>
				) : null}

				<section className="gm-section">
					{mode === "league" ? (
						<>
							<div className="gm-section-head">
								<h2>Start a new league</h2>
							</div>
							<p className="gm-grid-subhead">
								Choose one of 30 franchises to lead.
							</p>
						</>
					) : null}

					<div className="gm-search">
						<span className="gm-search-glyph">⌕</span>
						<input
							className="gm-search-input"
							placeholder="Search teams or cities"
							value={query}
							onChange={(e) => setQuery(e.target.value)}
						/>
						{query ? (
							<button
								type="button"
								className="gm-search-clear"
								aria-label="Clear search"
								onClick={() => setQuery("")}
							>
								✕
							</button>
						) : null}
					</div>

					{filtered.length === 0 && teams.length > 0 ? (
						<p className="gm-empty">No team matches “{query}”.</p>
					) : (
						<div className="gm-grid">
							{filtered.map((t, i) => {
								const color = t.colors?.[0] ?? "#1d3a52";
								const isSel = selected === t.tid;
								return (
									<button
										type="button"
										key={t.tid}
										className={`gm-cell ${isSel ? "sel" : ""}`}
										style={{ animationDelay: `${Math.min(i, 20) * 0.02}s` }}
										onClick={() => setSelected(isSel ? undefined : t.tid)}
									>
										<span className="gm-crest-wrap">
											<span
												className="gm-crest"
												style={{
													background: crest(color),
													color: crestText(color),
												}}
											>
												{t.abbrev}
											</span>
											{isSel ? <span className="gm-check">✓</span> : null}
										</span>
										<span className="gm-cell-name">
											{t.region} {t.name}
										</span>
									</button>
								);
							})}
						</div>
					)}
				</section>
			</div>

			{selectedTeam ? (
				<div className="gm-cta-wrap">
					<button
						type="button"
						className="gm-cta"
						onClick={
							mode === "challenge"
								? () => {
										if (!requireSlot()) {
											return;
										}
										setVerifyPath("create");
										setSheet("verify");
									}
								: start
						}
						disabled={creating}
					>
						<span className="gm-cta-text">
							<span className="gm-cta-over">
								{mode === "challenge"
									? "EXECUTIVE CHALLENGE · 2026–27"
									: "NEW LEAGUE · 2026–27"}
							</span>
							<span className="gm-cta-title">
								{creating
									? "Creating…"
									: mode === "challenge"
										? `Create Challenge as the ${selectedTeam.name}`
										: `Start with the ${selectedTeam.name}`}
							</span>
						</span>
						<span className="gm-cta-play">
							<span className="gm-tri gm-tri-green" />
						</span>
					</button>
				</div>
			) : null}

			{sheet === "join" ? (
				<ChallengeJoinSheet
					initialCode={deepLinkCode}
					onClose={() => setSheet(null)}
					resolveCode={async (code) => {
						const info = await toWorker("main", "getChallengeInfo", { code });
						setJoinTid(info.tid);
						return info;
					}}
					teamNameForTid={(tid) => {
						const t = teamByTid.get(tid);
						return t ? `${t.region} ${t.name}` : undefined;
					}}
					onContinue={(code) => {
						joinCode.current = code;
						setVerifyPath("join");
						setSheet("verify");
					}}
				/>
			) : null}

			{sheet === "verify" ? (
				<ChallengeVerify
					onClose={() => setSheet(null)}
					onVerified={onVerified}
				/>
			) : null}

			{sheet === "created" && created ? (
				<ChallengeCreatedSheet
					code={created.code}
					onCopy={() => copyInvite(created.code)}
					onStart={startManaging}
				/>
			) : null}

			{toast ? <div className="gm-toast">{toast}</div> : null}

			{confirmDelete ? (
				<div
					className="gm-sheet-backdrop"
					onClick={() => setConfirmDelete(undefined)}
				>
					<div className="gm-sheet" onClick={(e) => e.stopPropagation()}>
						<span
							className="gm-crest gm-crest-sheet"
							style={{
								background: crest(
									teamByTid.get(confirmDelete.tid)?.colors?.[0] ?? "#1d3a52",
								),
								color: crestText(
									teamByTid.get(confirmDelete.tid)?.colors?.[0] ?? "#1d3a52",
								),
							}}
						>
							{teamByTid.get(confirmDelete.tid)?.abbrev ??
								confirmDelete.teamName.slice(0, 3).toUpperCase()}
						</span>
						<h3 className="gm-sheet-title">Delete this league?</h3>
						<p className="gm-sheet-sub">
							{confirmDelete.teamRegion} {confirmDelete.teamName}
						</p>
						<p className="gm-sheet-warn">
							You’ll permanently lose every season, player, and game from this
							league. This can’t be undone.
						</p>
						<button
							type="button"
							className="gm-sheet-delete"
							onClick={doDelete}
						>
							Delete league
						</button>
						<button
							type="button"
							className="gm-sheet-keep"
							onClick={() => setConfirmDelete(undefined)}
						>
							Keep it
						</button>
					</div>
				</div>
			) : null}

			{showLimit ? (
				<div className="gm-sheet-backdrop" onClick={() => setShowLimit(false)}>
					<div className="gm-sheet" onClick={(e) => e.stopPropagation()}>
						<span
							className="gm-crest gm-crest-sheet"
							style={{ background: "#1d3a52", color: "#7fc4e8" }}
						>
							{leagues.length}/{MAX_LEAGUES}
						</span>
						<h3 className="gm-sheet-title">League limit reached</h3>
						<p className="gm-sheet-sub">
							You can keep up to {MAX_LEAGUES} leagues at a time.
						</p>
						<p className="gm-sheet-warn">
							Delete one of your saved leagues below to free up a slot, then
							start your new one.
						</p>
						<button
							type="button"
							className="gm-sheet-keep"
							onClick={() => setShowLimit(false)}
						>
							Got it
						</button>
					</div>
				</div>
			) : null}

			{challengeError ? (
				<div
					className="gm-sheet-backdrop"
					onClick={() => setChallengeError(undefined)}
				>
					<div className="gm-sheet" onClick={(e) => e.stopPropagation()}>
						<span
							className="gm-crest gm-crest-sheet"
							style={{ background: "#3a1f22", color: "#E2504E" }}
						>
							!
						</span>
						<h3 className="gm-sheet-title">
							Couldn’t {challengeError.verb} the challenge
						</h3>
						<p className="gm-sheet-sub">
							Something went wrong reaching the server.
						</p>
						<p className="gm-sheet-warn">
							Check your connection and try again. If it keeps happening, fully
							close and reopen the app to get the latest version.
						</p>
						{challengeError.detail ? (
							<p
								className="gm-sheet-warn"
								style={{ opacity: 0.6, fontSize: 11 }}
							>
								{challengeError.detail}
							</p>
						) : null}
						<button
							type="button"
							className="gm-sheet-delete"
							style={{ background: "#18E0A8", color: "#053a2c" }}
							onClick={() => {
								const { retry } = challengeError;
								setChallengeError(undefined);
								retry();
							}}
						>
							Try again
						</button>
						<button
							type="button"
							className="gm-sheet-keep"
							onClick={() => setChallengeError(undefined)}
						>
							Close
						</button>
					</div>
				</div>
			) : null}
		</div>
	);
};

const hubStyles = `
.sl-hub { min-height:100vh; background:#101214; color:#fff; font-family:Montserrat, system-ui, sans-serif;
  -webkit-font-smoothing:antialiased; }
.sl-hub-shell { max-width:980px; margin:0 auto; padding:24px 16px 42px; }
.sl-hub-head { display:flex; align-items:center; justify-content:space-between; gap:16px; margin-bottom:34px; }
.sl-hub-brand { display:flex; align-items:center; gap:12px; color:#fff; text-decoration:none; }
.sl-hub-brand:hover { color:#fff; text-decoration:none; }
.sl-hub-brand b { display:block; font-size:20px; font-weight:900; letter-spacing:.5px; line-height:1; }
.sl-hub-brand i { display:block; margin-top:3px; color:#18E0A8; font-size:12px; font-style:normal;
  font-weight:900; letter-spacing:5px; line-height:1; }
.sl-hub-intro { max-width:620px; margin-bottom:20px; }
.sl-hub-kicker { color:#18E0A8; font-size:12px; font-weight:900; letter-spacing:1.1px; text-transform:uppercase; }
.sl-hub-intro h1 { margin:6px 0 0; color:#fff; font-size:42px; font-weight:900; letter-spacing:0;
  line-height:1.02; }
.sl-game-grid { display:grid; grid-template-columns:repeat(3, minmax(0, 1fr)); gap:12px; }
.sl-game-grid-more { margin-top:14px; }
.sl-game-card { position:relative; display:grid; gap:18px; min-height:300px; padding:18px;
  border:1px solid #28313a; border-top:4px solid var(--accent); border-radius:8px;
  background:#161a1f; color:#fff; text-decoration:none; overflow:hidden; }
.sl-game-card:hover { color:#fff; text-decoration:none; border-color:var(--accent); }
.sl-game-time { position:absolute; top:14px; right:14px; display:grid; justify-items:end; gap:1px;
  min-width:64px; padding:7px 9px; background:#101418; border:1px solid var(--accent);
  border-radius:8px; box-shadow:0 8px 18px rgba(0,0,0,.24); }
.sl-game-time small { color:#91a0aa; font-size:9px; font-weight:900; letter-spacing:.7px; text-transform:uppercase; }
.sl-game-time strong { color:var(--accent); font-size:13px; font-weight:900; line-height:1; }
.sl-game-copy { display:grid; align-content:start; gap:7px; padding-right:78px; }
.sl-game-kicker { color:var(--accent); font-size:11px; font-weight:900; letter-spacing:.8px; text-transform:uppercase; }
.sl-game-copy strong { color:#fff; font-size:28px; font-weight:900; letter-spacing:0; line-height:1; }
.sl-game-copy span:last-child { color:#adbac4; font-size:13px; font-weight:650; line-height:1.45; }
.sl-game-meta { display:flex; flex-wrap:wrap; gap:6px; align-content:end; }
.sl-game-meta small { display:inline-flex; align-items:center; min-height:24px; padding:4px 8px;
  background:#20272e; border:1px solid #303a44; border-radius:999px; color:#c7d1d8; font-size:10px; font-weight:800; }
.sl-game-meta small.sl-game-meta-multiplayer { background:rgba(24,224,168,.14); border-color:rgba(24,224,168,.55);
  color:#18E0A8; }
.sl-game-open { align-self:end; display:flex; align-items:center; justify-content:center; min-height:40px;
  background:var(--accent); border-radius:999px; color:#081014; font-size:13px; font-weight:900; }
.sl-see-more { display:flex; align-items:center; justify-content:center; min-height:34px; margin:12px auto 0;
  padding:0 14px; border-radius:999px; background:transparent; border:1px solid rgba(255,255,255,.12);
  color:#8f9baa; font-family:inherit; font-size:12px; font-weight:850; cursor:pointer; }
.sl-see-more:hover { border-color:rgba(255,255,255,.22); color:#c7d1d8; }
@media (max-width: 760px) {
  .sl-hub-shell { padding:22px 14px 30px; }
  .sl-hub-head { margin-bottom:26px; }
  .sl-hub-intro h1 { font-size:34px; }
  .sl-game-grid { grid-template-columns:1fr; }
  .sl-game-card { min-height:180px; gap:14px; }
}
`;

const styles = `
.gm-start { position:relative; min-height:100vh; background:#121212; color:#fff;
  font-family:Montserrat, system-ui, sans-serif; -webkit-font-smoothing:antialiased; }

/* League-creation splash — the boot animation replayed while the league is built. */
.gm-splash { position:fixed; inset:0; z-index:3000; display:flex; flex-direction:column;
  align-items:center; justify-content:center; text-align:center;
  background:radial-gradient(125% 82% at 50% 33%,#3a1550 0%,#251438 42%,#121212 80%); }
.gm-splash-pop { animation:gmslPop .7s cubic-bezier(.2,.9,.3,1.2) both; }
.gm-splash-bob { animation:gmslBob 2.4s ease-in-out infinite; }
.gm-splash-word { margin-top:24px; animation:gmslFade .6s ease .5s both; }
.gm-splash-word b { display:block; font-size:30px; font-weight:900; letter-spacing:1px; color:#fff; line-height:1; }
.gm-splash-word i { display:block; font-size:18px; font-weight:900; font-style:normal; letter-spacing:9px;
  color:#18E0A8; margin-top:5px; }
.gm-splash-bar { width:150px; height:4px; border-radius:3px; background:rgba(255,255,255,.14);
  overflow:hidden; margin-top:30px; }
.gm-splash-fill { height:100%; border-radius:3px;
  background:linear-gradient(90deg,#7C36E6,#FF3D86,#FF7A3C,#18E0A8); animation:gmslBar 1.9s ease-in-out both; }
.gm-splash-cap { font-size:10.5px; font-weight:700; letter-spacing:1.5px; color:#9a8fb0; margin-top:14px;
  animation:gmslFade .6s ease .8s both; }
@keyframes gmslPop { 0%{transform:scale(.4);opacity:0} 60%{transform:scale(1.08);opacity:1} 100%{transform:scale(1);opacity:1} }
@keyframes gmslBob { 0%,100%{transform:translateY(0)} 50%{transform:translateY(-8px)} }
@keyframes gmslBar { 0%{width:0} 90%,100%{width:100%} }
@keyframes gmslFade { from{opacity:0;transform:translateY(10px)} to{opacity:1;transform:none} }
.gm-start-header { position:absolute; top:0; left:0; right:0; height:280px; pointer-events:none;
  background:linear-gradient(180deg,#3a1550 0%,#4a1c46 32%,#241730 62%,#121212 100%); }
.gm-start-body { position:relative; z-index:1; padding:24px 16px 130px; }

.gm-brand { display:flex; align-items:center; gap:12px; margin-bottom:20px; }
.gm-brand-logo { flex:none; display:block; filter:drop-shadow(0 4px 12px rgba(124,54,230,.35)); }
.gm-brand-word { line-height:1; flex:1; min-width:0; }
.gm-brand-word b { display:block; font-size:21px; font-weight:900; letter-spacing:.5px; color:#fff; }
.gm-brand-word i { display:block; font-size:13px; font-weight:900; font-style:normal;
  letter-spacing:5.5px; color:#18E0A8; margin-top:3px; }
.gm-main-return { flex:none; display:inline-flex; align-items:center; justify-content:center; min-height:34px;
  padding:0 12px; border-radius:999px; background:rgba(28,28,28,.72); border:1px solid rgba(255,255,255,.12);
  color:#cfe0ec; font-size:12px; font-weight:850; text-decoration:none; }
.gm-main-return:hover { color:#fff; border-color:rgba(24,224,168,.42); text-decoration:none; }
.gm-headline { font-size:34px; font-weight:900; letter-spacing:-1.4px; line-height:1.02;
  margin:0 0 9px; color:#fff; }
.gm-subhead { font-size:13.5px; font-weight:600; color:#9fb0bc; line-height:1.5; margin:0 0 22px; }

.gm-section { margin-top:26px; }
.gm-section-head { display:flex; align-items:baseline; justify-content:space-between; }
.gm-section-head h2 { font-size:17px; font-weight:800; margin:0; color:#fff; }
.gm-section-meta { font-size:11px; font-weight:700; color:#8a8a8a; }
.gm-grid-subhead { font-size:11.5px; font-weight:600; color:#8a8a8a; margin:4px 0 12px; }

.gm-league-row { display:flex; align-items:center; gap:12px; background:#181818;
  border:1px solid #232323; border-radius:13px; padding:11px 12px; margin-top:9px;
  text-decoration:none; color:inherit; animation:gmFadeUp .3s ease both; }
.gm-league-row:hover { text-decoration:none; color:inherit; border-color:#2f2f2f; }
.gm-league-info { flex:1; min-width:0; display:flex; flex-direction:column; gap:2px; }
.gm-league-name { font-size:14.5px; font-weight:800; color:#fff; white-space:nowrap;
  overflow:hidden; text-overflow:ellipsis; }
.gm-league-metaline { font-size:11.5px; font-weight:600; color:#9a9a9a; }

.gm-crest { display:flex; align-items:center; justify-content:center; border-radius:50%;
  color:#fff; font-weight:900; letter-spacing:.3px; flex:none;
  box-shadow:inset 0 1px 0 rgba(255,255,255,.18), 0 4px 10px rgba(0,0,0,.35); }
.gm-crest-row { width:46px; height:46px; font-size:13px; }
.gm-crest-sheet { width:56px; height:56px; font-size:15px; margin:0 auto 14px; }

.gm-play { display:flex; align-items:center; justify-content:center; border-radius:50%;
  background:#18E0A8; flex:none; }
.gm-play-sm { width:38px; height:38px; }
.gm-tri { width:0; height:0; border-style:solid; border-width:7px 0 7px 12px;
  border-color:transparent transparent transparent #041b0b; margin-left:2px; }
.gm-tri-green { border-left-color:#18E0A8; }
.gm-trash { width:38px; height:38px; border-radius:50%; background:transparent; border:none;
  color:#7f8b93; font-size:15px; flex:none; cursor:pointer; }
.gm-trash:hover { color:#cdd4da; }

.gm-search { display:flex; align-items:center; gap:8px; background:#1c1c1c; border:1px solid #2a2a2a;
  border-radius:11px; padding:10px 13px; margin-bottom:6px; }
.gm-search-glyph { color:#7f8b93; font-size:15px; }
.gm-search-input { flex:1; background:transparent; border:none; outline:none; color:#fff;
  font-family:inherit; font-size:13.5px; font-weight:600; }
.gm-search-input::placeholder { color:#7f8b93; font-weight:600; }
.gm-search-clear { background:transparent; border:none; color:#7f8b93; font-size:13px; cursor:pointer; }

.gm-grid { display:grid; grid-template-columns:repeat(3,1fr); gap:10px; padding:12px 0 4px; }
.gm-cell { display:flex; flex-direction:column; align-items:center; padding:10px 4px 12px;
  border-radius:14px; background:transparent; border:1px solid transparent; cursor:pointer;
  animation:gmFadeUp .3s ease both; }
.gm-cell.sel { background:rgba(24,224,168,.12); border-color:rgba(24,224,168,.5); }
.gm-crest-wrap { position:relative; }
.gm-crest-wrap .gm-crest { width:62px; height:62px; font-size:16px; }
.gm-cell.sel .gm-crest { outline:2px solid #18E0A8; outline-offset:2px; }
.gm-check { position:absolute; right:-2px; bottom:-2px; width:22px; height:22px; border-radius:50%;
  background:#18E0A8; border:2px solid #121212; color:#000; font-size:12px; font-weight:900;
  display:flex; align-items:center; justify-content:center; }
.gm-cell-name { margin-top:8px; font-size:10.5px; font-weight:700; color:#cdd4da; text-align:center;
  line-height:1.15; }
.gm-empty { text-align:center; font-size:13px; font-weight:600; color:#7a7a7a; padding:28px 0; }

.gm-cta-wrap { position:fixed; left:0; right:0; bottom:0; z-index:40; padding:20px 16px 22px;
  background:linear-gradient(180deg, rgba(18,18,18,0), #121212 32%); max-width:760px; margin:0 auto; }
.gm-cta { display:flex; align-items:center; justify-content:space-between; width:100%;
  background:#18E0A8; border:none; border-radius:32px; box-shadow:0 8px 26px rgba(24,224,168,.32);
  padding:6px 6px 6px 16px; cursor:pointer; }
.gm-cta:disabled { opacity:.75; cursor:default; }
.gm-cta-text { display:flex; flex-direction:column; align-items:flex-start; min-width:0; }
.gm-cta-over { font-size:10px; font-weight:800; letter-spacing:.8px; color:#053a2c; }
.gm-cta-title { font-size:15px; font-weight:900; color:#000; white-space:nowrap; overflow:hidden;
  text-overflow:ellipsis; max-width:64vw; }
.gm-cta-play { width:46px; height:46px; border-radius:50%; background:#000; flex:none;
  display:flex; align-items:center; justify-content:center; }

.gm-sheet-backdrop { position:fixed; inset:0; z-index:60; background:rgba(0,0,0,.6);
  display:flex; align-items:flex-end; animation:gmFadeBg .2s ease; }
.gm-sheet { width:100%; max-width:760px; margin:0 auto; background:#1c1c1c;
  border-radius:22px 22px 0 0; padding:26px 20px max(26px, env(safe-area-inset-bottom));
  text-align:center; animation:gmSheetUp .28s cubic-bezier(.2,.9,.3,1); }
.gm-sheet-title { font-size:19px; font-weight:900; margin:0 0 4px; color:#fff; }
.gm-sheet-sub { font-size:13px; font-weight:700; color:#cdd4da; margin:0 0 10px; }
.gm-sheet-warn { font-size:12.5px; font-weight:600; color:#9a9a9a; line-height:1.4; margin:0 0 20px; }
.gm-sheet-delete { width:100%; background:#E03A3E; color:#fff; border:none; border-radius:26px;
  padding:14px; font-family:inherit; font-size:15px; font-weight:800; cursor:pointer; margin-bottom:10px; }
.gm-sheet-keep { width:100%; background:transparent; color:#cdd4da; border:none; padding:12px;
  font-family:inherit; font-size:14px; font-weight:700; cursor:pointer; }

/* Executive Challenge entry card + host-mode header */
.gm-ch-card { position:relative; border-radius:18px; overflow:hidden; background:#181818;
  border:1px solid #2a2340; margin:14px 0 2px; }
.gm-ch-spectrum { height:3px; background:linear-gradient(90deg,#7C36E6,#FF3D86,#FF7A3C,#18E0A8); }
.gm-ch-glow { position:absolute; inset:0; pointer-events:none;
  background:radial-gradient(115% 85% at 82% 6%,rgba(58,21,80,.9),rgba(37,20,56,.4) 46%,rgba(18,18,18,0) 74%); }
.gm-ch-card-body { position:relative; padding:15px 16px 16px; }
.gm-ch-eyebrow { display:flex; align-items:center; gap:8px; font-size:10px; font-weight:800;
  letter-spacing:1.1px; color:#18E0A8; }
.gm-ch-new { margin-left:auto; font-size:9px; font-weight:800; letter-spacing:.5px; color:#053a2c;
  background:#18E0A8; padding:3px 7px; border-radius:6px; }
.gm-ch-card-title { font-size:19px; font-weight:800; letter-spacing:-.4px; line-height:1.12;
  margin-top:11px; color:#fff; }
.gm-ch-card-sub { font-size:12.5px; color:#9fb0bc; font-weight:600; line-height:1.45; margin-top:6px; }
.gm-ch-actions { display:flex; align-items:center; gap:9px; margin-top:14px; }
.gm-ch-start { flex:1; display:flex; align-items:center; justify-content:center; gap:7px;
  padding:12px; border:none; border-radius:24px; background:#18E0A8; color:#053a2c; font-size:14px;
  font-weight:900; cursor:pointer; font-family:inherit; box-shadow:0 8px 22px rgba(24,224,168,.28);
  white-space:nowrap; }
.gm-ch-join { padding:12px 16px; border-radius:24px; background:#1c1c1c; border:1px solid #2a2a2a;
  color:#cfe0ec; font-size:13.5px; font-weight:800; cursor:pointer; font-family:inherit;
  white-space:nowrap; }
.gm-ch-join span { color:#18E0A8; }
.gm-ch-top-actions { display:flex; align-items:center; justify-content:space-between; gap:10px; margin-bottom:12px; }
.gm-ch-back { background:none; border:none; padding:0; color:#9fb0bc; font-size:13px;
  font-weight:800; cursor:pointer; font-family:inherit; display:block; text-decoration:none; }
.gm-ch-back:hover { color:#cfe0ec; text-decoration:none; }
.gm-ch-route-join { padding:8px 12px; border-radius:999px; background:#1c1c1c; border:1px solid #2a2a2a;
  color:#cfe0ec; font-size:12px; font-weight:800; cursor:pointer; font-family:inherit; white-space:nowrap; }
.gm-ch-route-join span { color:#18E0A8; }
.gm-headline-ch { font-size:27px; letter-spacing:-.7px; }
.gm-ch-free { display:inline-flex; align-items:center; gap:6px; margin:0 0 8px;
  background:rgba(24,224,168,.1); border:1px solid rgba(24,224,168,.28); border-radius:12px;
  padding:6px 11px; font-size:11px; font-weight:800; letter-spacing:.2px; color:#18E0A8; }

.gm-toast { position:fixed; left:50%; bottom:120px; z-index:2200; transform:translateX(-50%);
  background:#fff; color:#000; font-size:12.5px; font-weight:800; padding:11px 18px;
  border-radius:24px; box-shadow:0 10px 30px rgba(0,0,0,.5); white-space:nowrap;
  animation:gmToastIn .3s ease both; }
@keyframes gmToastIn { from { opacity:0; transform:translate(-50%,14px); }
  to { opacity:1; transform:translate(-50%,0); } }

@keyframes gmFadeUp { from { opacity:0; transform:translateY(10px); } to { opacity:1; transform:none; } }
@keyframes gmFadeBg { from { opacity:0; } to { opacity:1; } }
@keyframes gmSheetUp { from { transform:translateY(100%); } to { transform:none; } }
`;

const GmStart = (props: DashboardView) => {
	if (window.location.pathname === "/") {
		return <SiteHub />;
	}

	if (window.location.pathname === "/challenge") {
		return <DynastyStart {...props} challengeRoute />;
	}

	return <DynastyStart {...props} />;
};

export default GmStart;
